import React, {useCallback, useEffect, useState} from 'react';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Tooltip,
} from '@mui/material';
import EditIcon from "@material-ui/icons/Edit";
import useStyles from '../../assets/styles/HomeStyles';
import {
    assignLicensesService,
    createClientService, editClientService, getLinkedAssessmentService,
    linkAssessmentService,
    listClientsService
} from "../../services/client.service";
import CreateClientDialog from "./components/CreateClientDialog.comp";
import LoaderWithBackDrop from "../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import {toast} from "../../utils/utils";
import {TOAST_TYPES} from "../../utils/constants";
import AssignLicensesDialog from "./components/AssignLicensesDialog.comp";
import LinkAssessmentDialog from "./components/LinkAssessmentDialog.comp";
import {getInsiderAssessmentsService} from "../../services/assessments.service";
import {countries} from "../../utils/countries";
import LinkProgramDialog from "./components/LinkProgramDialog.comp";
import {
    getInsiderProgramsService,
    getLinkedLearningProgramService,
    linkLearningProgramService
} from "../../services/learning_programs.service";
import EditClientDialog from "./components/EditClientDialog.comp";

const DQProClientList = () => {
    const classes = useStyles();
    const [loading, setLoading] = useState(true);
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [creating, setCreating] = useState(false);
    const [clientData, setClientData] = useState([]);

    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [assignOrg, setAssignOrg] = useState({org_code: '', org_name: ''});
    const [assigning, setAssigning] = useState(false);

    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [linkOrg, setLinkOrg] = useState({org_code: '', org_name: ''});
    const [linking, setLinking] = useState(false);
    const [linkedAssessments, setLinkedAssessments] = useState([]);
    const [linkingLoading, setLinkingLoading] = useState(false);

    const [assessments, setAssessments] = useState([]);

    const [linkLearningDialogOpen, setLinkLearningDialogOpen] = useState(false);
    const [learningPrograms, setLearningPrograms] = useState([]);
    const [linkedLearningCodes, setLinkedLearningCodes] = useState([]);
    const [linkLearningLoading, setLinkLearningLoading] = useState(false);
    const [linkingLearning, setLinkingLearning] = useState(false);
    const [learningOrg, setLearningOrg] = useState({ org_code: '', org_name: '' });

    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        getInsiderAssessmentsService().then(res => {
            setAssessments(res?.data.assessments || []);
        });
    }, []);

    useEffect(() => {
        getInsiderProgramsService().then(res => {
            setLearningPrograms(res?.data.learning_programs || [])
        });
    }, []);


    const fetchClients = useCallback(async () => {
        setLoading(true);
        try {
            const response = await listClientsService();
            if (response && response.data) {
                setClientData(response.data);
            }
        } catch (err) {
            setClientData([]);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchClients().then(r => console.log(r));
    }, [fetchClients]);

    const handleCreateClient = async (fields) => {
        setCreating(true);
        setLoading(true)
        try {
            const payload = { ...fields };
            const response = await createClientService(payload);
            if (response?.data?.success) {
                setOpenCreateDialog(false);
                toast(TOAST_TYPES.SUCCESS, "Client Created", "Successfully Created a Client");
                await fetchClients(); // REFRESH
            }
        } catch (err) {
            console.log('Failed to create client!');
        } finally {
            setCreating(false);
            setLoading(false);
        }
        return true;
    };

    const handleEditClient = async (formData) => {
        setEditing(true);
        setLoading(true);
        try {
            const response = await editClientService(editingClient.org_code, formData);
            if (response?.data?.success) {
                toast(TOAST_TYPES.SUCCESS, "Success", "Client updated successfully");
                setEditDialogOpen(false);
                await fetchClients(); // refresh
            }
            console.log('Editing client with data:', formData);
            console.log('Client org_code:', editingClient?.org_code);
        } catch (err) {
            toast(TOAST_TYPES.DANGER, "Failed", "Failed to update client");
        } finally {
            setEditing(false);
            setLoading(false);
        }
    };

    const handleOpenEdit = (client) => {
        setEditingClient(client);
        setEditDialogOpen(true);
    };

    const handleOpenAssign = (client) => {
        setAssignOrg({ org_code: client.org_code, org_name: client.org_name });
        setAssignDialogOpen(true);
    };

    const handleAssignLicenses = async ({ org_code, license_count }) => {
        setAssigning(true);
        setLoading(true);
        try {
            const response = await assignLicensesService(org_code, license_count);
            if (response?.data?.success) {
                toast(TOAST_TYPES.SUCCESS, "Licenses Assigned", "Successfully assigned licenses!");
                setAssignDialogOpen(false);
                await fetchClients(); // Refresh list if needed
            }
        } catch (e) {
            toast(TOAST_TYPES.DANGER, "Failed", "Failed to assign licenses!");
        } finally {
            setAssigning(false);
            setLoading(false);
        }
    };

    const handleLinkAssessments = async (orgCode, selectedCodes) => {
        setLinking(true);
        setLoading(true);
        try {
            const response = await linkAssessmentService(orgCode, selectedCodes);
            if (response?.data?.success) {
                toast(TOAST_TYPES.SUCCESS, "Success", "Assessments linked successfully");
                await fetchClients(); // refresh
            }
            setLinkDialogOpen(false);
        } catch (err) {
            toast(TOAST_TYPES.DANGER, "Failed", "Failed to link assessments");
        } finally {
            setLinking(false);
            setLoading(false);
        }
    };

    const handleOpenLinkLearning = async (client) => {
        setLearningOrg({ org_code: client.org_code, org_name: client.org_name });
        setLinkLearningDialogOpen(true);
        setLinkLearningLoading(true);
        try {
            const res = await getLinkedLearningProgramService(client.org_code);
            if (res?.data?.linked_learning_programs) {
                setLinkedLearningCodes(res.data.linked_learning_programs.map(a => a.code));
            } else {
                setLinkedLearningCodes([]);
            }
        } catch (err) {
            setLinkedLearningCodes([]);
        }
        setLinkLearningLoading(false);
    };


    const handleOpenLink = async (client) => {
        setLinkOrg({org_code: client.org_code, org_name: client.org_name});
        setLinkDialogOpen(true);
        setLinkingLoading(true);
        setLoading(true);
        try {
            const res = await getLinkedAssessmentService(client.org_code);
            if (res?.data?.linked_assessments) {
                setLinkedAssessments(res.data.linked_assessments.map(a => a.code));
            } else {
                setLinkedAssessments([]);
            }
        } catch (err) {
            setLinkedAssessments([]);
        } finally {
            setLinkingLoading(false);
            setLoading(false);
        }
    };

    const handleLinkLearningPrograms = async (orgCode, selectedCodes) => {
        setLinkingLearning(true);
        setLoading(true);
        try {
            const response = await linkLearningProgramService(orgCode, selectedCodes);
            if (response?.data?.success) {
                toast(TOAST_TYPES.SUCCESS, "Success", "Learning Programs linked successfully");
                await fetchClients(); // refresh
            }
            setLinkLearningDialogOpen(false);
        } catch (err) {
            toast(TOAST_TYPES.DANGER, "Failed", "Failed to link learning programs");
        } finally {
            setLinkingLearning(false);
            setLoading(false);
        }
    };


    const countryCodeToName = Object.fromEntries(
        countries.map(({ code, label }) => [code, label])
    );

    // Helper function to check if client has enough licenses to link assessments/programs
    const hasEnoughLicenses = (client) => {
        if (client.licenses_total === 0) {
            return false;
        }
        const licensesRemaining = client.licenses_total - client.licenses_used;
        return licensesRemaining >= client.users;
    };

    return (
        <Box className={classes.pageContainer} sx={{
            overflowX: 'hidden',
            width: '100%',
            px: { xs: 0, sm: 2 },
            mt:{sm:'75px', lg:'auto'}
        }}>
            <Typography variant="h2" className={classes.title} sx={{ mb: 2 }}>
                Your Client List
            </Typography>
            <Typography variant="h5" sx={{ fontSize:{xs:'18px', sm:'1.5rem'},textAlign: 'left',p:{xs:'0 20px', sm:'0 30px', lg:'0 170px'}, mb: 4 }}>
                Here you can add new clients, manage the products your client has access to, and assign specific number of licenses to your clients.            </Typography>
            <Button
                variant="text"
                size="small"
                sx={{
                    backgroundColor: '#ff6468',
                    color: '#ffffff',
                    fontWeight: 'bold',
                    fontSize: '19px',
                    borderRadius: '20px',
                    boxShadow: 'none',
                    px: 4,
                    mb:4,
                    '&:hover': {
                        backgroundColor: '#ff6468',
                    },
                }}
                onClick={() => setOpenCreateDialog(true)}
            >
                Add a New Client
            </Button>

            <TableContainer
                sx={{
                    border: '1px solid #e0e0e0',
                    width: '100%',
                    maxWidth: '100vw',
                    overflowX: 'auto',
                }}
            >
                    <Table sx={{
                    // minWidth: 700,
                    width: '100%',
                    tableLayout: 'fixed',
                    }}>
                    <TableHead sx={{backgroundColor: '#000000'}}>
                        <TableRow sx={{backgroundColor: '#000000 !important'}}>
                            <TableCell
                                sx={{
                                    borderRight: '1px solid #ddd',
                                    fontSize: {xs:'10px', sm:'16px'},
                                    fontWeight: 'bold',
                                    color: '#ffffff !important',
                                    width: {xs:'unset', sm:'18%', md:'30%'},
                                    textAlign: 'center',
                                }}
                            >
                                Organization Name
                            </TableCell>
                            <TableCell
                                sx={{
                                    borderRight: '1px solid #ddd',
                                    fontSize: {xs:'10px', sm:'16px'},
                                    fontWeight: 'bold',
                                    color: '#ffffff !important',
                                    width: {xs:'unset',sm: '15%'},
                                    textAlign: 'center',
                                }}
                            >
                                Country
                            </TableCell>
                            <TableCell
                                sx={{
                                    borderRight: '1px solid #ddd',
                                    fontSize: {xs:'10px', sm:'16px'},
                                    fontWeight: 'bold',
                                    color: '#ffffff !important',
                                    width: {xs:'unset',sm: '10%'},
                                    textAlign: 'center',
                                }}
                            >
                                # of Individual Users
                            </TableCell>
                            <TableCell
                                sx={{
                                    borderRight: '1px solid #ddd',
                                    fontSize: {xs:'10px', sm:'16px'},
                                    fontWeight: 'bold',
                                    color: '#ffffff !important',
                                    width: {xs:'unset',sm: '15%'},
                                    textAlign: 'center',
                                }}
                            >
                                # of Used/Assigned Licenses
                            </TableCell>
                            <TableCell
                                sx={{
                                    fontSize: {xs:'10px', sm:'16px'},
                                    fontWeight: 'bold',
                                    color: '#ffffff !important',
                                    width: {xs:'unset', sm:'24%', md:'30%'},
                                    textAlign: 'center',
                                }}
                            >
                                Assign Licenses and Interventions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {clientData.map((client, index) => (
                            <TableRow key={index}>
                                <TableCell
                                    sx={{
                                        fontSize: {xs:'11px', sm:'17px'},
                                        borderRight: '1px solid #ddd',
                                        textAlign: 'left',
                                        px: 2,
                                    }}
                                >
                                    <Box sx={{ml:'70px', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                                        <EditIcon onClick={() => handleOpenEdit(client)} className={classes.editIcon}/>
                                        <span>{client.org_name}</span>
                                    </Box>
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontSize: {xs:'11px', sm:'17px'},
                                        textAlign: 'center',
                                        borderRight: '1px solid #ddd'
                                    }}
                                >
                                    {countryCodeToName[client.country] || client.country}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontSize: {xs:'11px', sm:'17px'},
                                        textAlign: 'center',
                                        borderRight: '1px solid #ddd'
                                    }}
                                >
                                    {client.users}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontSize: {xs:'11px', sm:'17px'},
                                        textAlign: 'center',
                                        borderRight: '1px solid #ddd'
                                    }}
                                >
                                <span style={{fontWeight: 'bold', color: '#ff6468'}}>
                                    {client.licenses_used}
                                  </span>
                                    {' / '}
                                    {client.licenses_total}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        width: { xs: '100%', sm: '24%', md: '30%' },
                                        textAlign: 'center',
                                        display: { xs: 'block', sm: 'table-cell' },
                                        borderRight: { sm: 'none' },
                                        px: { xs: 1, sm: 2 },
                                    }}>
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1,
                                    }}>
                                            <Button
                                                variant="text"
                                                size="small"
                                                sx={{
                                                    width: { xs: '100%', sm: 'auto' },
                                                    minWidth: { xs: 'auto', sm: '160px' },
                                                    borderRadius: { xs: '8px', sm: '20px' },
                                                    backgroundColor: '#c7c804',
                                                    color: '#ffffff',
                                                    fontWeight: 'bold',
                                                    fontSize: {xs:'7px',sm:'13px', md:'16px'},
                                                    boxShadow: 'none',
                                                    py: { xs: 0.5, sm: 1 },
                                                    px: { xs: 1, sm: 2 },
                                                    '&:hover': {
                                                        backgroundColor: '#c7c804',
                                                        color: '#ffffff',
                                                    },
                                                }}
                                                onClick={() => handleOpenAssign(client)}
                                            >
                                                Assign Licenses
                                            </Button>
                                            <Tooltip
                                                title={!hasEnoughLicenses(client)
                                                    ? client.licenses_total === 0
                                                        ? "No licenses available. Please assign licenses first."
                                                        : "Not enough licenses remaining. Each user requires one license per assessment."
                                                    : ""}
                                                arrow
                                            >
                                                <span>
                                                    <Button
                                                        variant="text"
                                                        size="small"
                                                        disabled={!hasEnoughLicenses(client)}
                                                        sx={{
                                                            width: { xs: '100%', sm: 'auto' },
                                                            minWidth: { xs: 'auto', sm: '160px' },
                                                            borderRadius: { xs: '8px', sm: '20px' },
                                                            backgroundColor: '#f54ea2',
                                                            color: '#ffffff',
                                                            fontWeight: 'bold',
                                                            fontSize: {xs:'7px',sm:'13px', md:'16px'},
                                                            boxShadow: 'none',
                                                            py: { xs: 0.5, sm: 1 },
                                                            px: { xs: 1, sm: 2 },
                                                            '&:hover': {
                                                                backgroundColor: '#f54ea2',
                                                            },
                                                            '&.Mui-disabled': {
                                                                backgroundColor: '#cccccc',
                                                                color: '#666666',
                                                            },
                                                        }}
                                                        onClick={() => handleOpenLink(client)}
                                                    >
                                                        Link Assessment
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        <Tooltip
                                            title={!hasEnoughLicenses(client)
                                                ? client.licenses_total === 0
                                                    ? "No licenses available. Please assign licenses first."
                                                    : "Not enough licenses remaining. Each user requires one license per learning program."
                                                : ""}
                                            arrow
                                        >
                                            <span>
                                                <Button
                                                    variant="text"
                                                    size="small"
                                                    disabled={!hasEnoughLicenses(client)}
                                                    sx={{
                                                        width: { xs: '100%', sm: 'auto' },
                                                        minWidth: { xs: 'auto', sm: '160px' },
                                                        borderRadius: { xs: '8px', sm: '20px' },
                                                        backgroundColor: '#3366ff',
                                                        color: '#ffffff',
                                                        fontWeight: 'bold',
                                                        fontSize: {xs:'7px',sm:'13px', md:'16px'},
                                                        boxShadow: 'none',
                                                        py: { xs: 0.5, sm: 1 },
                                                        px: { xs: 1, sm: 2 },
                                                        '&:hover': {
                                                            backgroundColor: '#3366ff',
                                                        },
                                                        '&.Mui-disabled': {
                                                            backgroundColor: '#cccccc',
                                                            color: '#666666',
                                                        },
                                                        alignSelf: 'center'
                                                    }}
                                                    onClick={() => handleOpenLinkLearning(client)}
                                                >
                                                    Link Learning Program
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <LoaderWithBackDrop loading={loading} />
            <CreateClientDialog
                open={openCreateDialog}
                onClose={() => setOpenCreateDialog(false)}
                onSubmit={handleCreateClient}
                loading={creating}
            />
            <EditClientDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                onSubmit={handleEditClient}
                loading={editing}
                client={editingClient}
            />
            <AssignLicensesDialog
                open={assignDialogOpen}
                onClose={() => setAssignDialogOpen(false)}
                onSubmit={handleAssignLicenses}
                loading={assigning}
                orgName={assignOrg.org_name}
                orgCode={assignOrg.org_code}
            />
            <LinkAssessmentDialog
                assessments={assessments}
                open={linkDialogOpen}
                onSubmit={handleLinkAssessments}
                loading={linking}
                onClose={() => setLinkDialogOpen(false)}
                orgName={linkOrg.org_name}
                orgCode={linkOrg.org_code}
                linkedAssessmentCodes={linkedAssessments}
                onLinked={fetchClients} // optional, if you want to refresh list
            />
            <LinkProgramDialog
                programs={learningPrograms}
                open={linkLearningDialogOpen}
                onSubmit={handleLinkLearningPrograms}
                loading={linkingLearning}
                onClose={() => setLinkLearningDialogOpen(false)}
                orgName={learningOrg.org_name}
                orgCode={learningOrg.org_code}
                linkedProgramCodes={linkedLearningCodes}
                linkingLoading={linkLearningLoading}
            />

        </Box>
    );
};

export default DQProClientList;
